import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { MessageCircle, Copy, CheckCircle2, XCircle, Send, RefreshCw, ArrowLeft } from 'lucide-react';
import { adminAPI } from '@/services/adminAPI';
import Pagination from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

function CredBadge({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${ok ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {label}
    </span>
  );
}

function CopyField({ label, value, testId }) {
  const copy = () => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); };
  return (
    <div className="min-w-0 overflow-hidden flex items-center justify-between gap-3 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2">
      <div className="min-w-0">
        <p className="text-xs text-slate-500 dark:text-zinc-500">{label}</p>
        <p className="text-sm text-slate-900 dark:text-white truncate font-mono" data-testid={testId}>{value || '—'}</p>
      </div>
      {value && (
        <button onClick={copy} className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-zinc-800 text-slate-500 dark:text-zinc-400 shrink-0" data-testid={`${testId}-copy`}>
          <Copy className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default function AdminWhatsApp() {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [convs, setConvs] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [testPhone, setTestPhone] = useState('');
  const [testText, setTestText] = useState('');
  const [useTemplate, setUseTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c, t] = await Promise.all([
        adminAPI.getWhatsAppStatus(),
        adminAPI.listWhatsAppConversations({ page }),
        adminAPI.getWhatsAppTemplates(),
      ]);
      setStatus(s);
      setConvs(c);
      const list = t.data || [];
      setTemplates(list);
      setTemplateName((prev) => prev || (list.find((x) => x.status === 'APPROVED') || list[0])?.name || '');
    } catch {
      toast.error('Failed to load WhatsApp status');
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const openConv = async (phone) => {
    try {
      const r = await adminAPI.getWhatsAppMessages(phone);
      setSelected(phone);
      setMessages(r.items);
    } catch {
      toast.error('Failed to load messages');
    }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await adminAPI.sendWhatsAppTest({ phone: testPhone, text: testText, use_template: useTemplate, template_name: templateName });
      if (r.sent) toast.success(`Sent! ID: ${r.wa_message_id}`);
      else if (r.skipped) toast.warning('Not sent — WhatsApp credentials are not configured yet');
      else toast.error(`${r.error || 'Failed'}${r.hint ? ` — ${r.hint}` : ''}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    }
    setSending(false);
  };

  const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /></div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">WhatsApp Business</h1>
          <p className="text-sm text-slate-500 dark:text-zinc-500">Meta Cloud API — candidate assistant, profile updates & interview prep</p>
        </div>
        <button onClick={load} className="ml-auto p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500 dark:text-zinc-400" data-testid="wa-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {status && (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5 space-y-4" data-testid="wa-status-card">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-slate-900 dark:text-white">Connection status</h2>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.configured ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'}`} data-testid="wa-configured-badge">
              {status.configured ? 'Configured' : 'Awaiting credentials'}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <CredBadge ok={status.credentials.phone_number_id} label="Phone Number ID" />
            <CredBadge ok={status.credentials.waba_id} label="WABA ID" />
            <CredBadge ok={status.credentials.access_token} label="Access Token" />
            <CredBadge ok={status.credentials.app_secret} label="App Secret" />
            <CredBadge ok={status.credentials.verify_token} label="Verify Token" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <CopyField label="Webhook callback URL (paste in Meta console)" value={status.webhook_url} testId="wa-webhook-url" />
            <CopyField label="Webhook verify token" value={status.verify_token} testId="wa-verify-token" />
          </div>
          <p className="text-xs text-slate-500 dark:text-zinc-500">
            {status.counts.conversations} conversations · {status.counts.messages} messages logged
          </p>
        </div>
      )}

      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5" data-testid="wa-templates-card">
        <h2 className="font-medium text-slate-900 dark:text-white mb-1">Message templates</h2>
        <p className="text-xs text-slate-500 dark:text-zinc-500 mb-3">Required for business-initiated messages (OTP, match pings, reminders). Reviewed by Meta.</p>
        <div className="flex flex-wrap gap-2">
          {templates.length === 0 && <p className="text-sm text-slate-500 dark:text-zinc-500" data-testid="wa-no-templates">No templates found.</p>}
          {templates.map((t) => (
            <div key={t.id} className="max-w-full flex items-center flex-wrap gap-x-2 gap-y-1 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2" data-testid={`wa-template-${t.name}`}>
              <span className="text-sm font-mono text-slate-900 dark:text-white break-all">{t.name}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-zinc-500">{t.category}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${t.status === 'APPROVED' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : t.status === 'REJECTED' ? 'bg-red-500/15 text-red-600 dark:text-red-400' : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'}`}>{t.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5" data-testid="wa-test-card">
        <h2 className="font-medium text-slate-900 dark:text-white mb-3">Send a test message</h2>
        <form onSubmit={sendTest} className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
          <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Phone e.g. 919876543210" required
            className="sm:w-56 min-w-0 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none" data-testid="wa-test-phone-input" />
          {useTemplate ? (
            <select value={templateName} onChange={(e) => setTemplateName(e.target.value)} required
              className="flex-1 min-w-0 sm:min-w-[220px] bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none" data-testid="wa-test-template-select">
              {templates.length === 0 && <option value="">No templates available</option>}
              {templates.map((t) => (
                <option key={t.id} value={t.name} disabled={t.status !== 'APPROVED'}>
                  {t.name} — {t.category}{t.status !== 'APPROVED' ? ` (${t.status})` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input value={testText} onChange={(e) => setTestText(e.target.value)} placeholder="Message text"
              className="flex-1 min-w-0 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none" data-testid="wa-test-text-input" />
          )}
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-zinc-400 shrink-0">
            <input type="checkbox" checked={useTemplate} onChange={(e) => setUseTemplate(e.target.checked)} data-testid="wa-test-template-checkbox" /> Template
          </label>
          <button type="submit" disabled={sending || (useTemplate && !templateName)} className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 shrink-0" data-testid="wa-test-send-btn">
            <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
        {useTemplate && templateName && (
          <p className="mt-2 text-xs text-slate-500 dark:text-zinc-500" data-testid="wa-test-template-hint">
            Sends the <span className="font-mono">{templateName}</span> template with sample values (OTP templates use code 123456). Only APPROVED templates can be sent.
          </p>
        )}
      </div>

      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5" data-testid="wa-conversations-card">
        {selected ? (
          <div>
            <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white mb-4" data-testid="wa-back-to-list">
              <ArrowLeft className="w-4 h-4" /> All conversations
            </button>
            <h2 className="font-medium text-slate-900 dark:text-white mb-4">+{selected}</h2>
            <div className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="wa-messages-list">
              {messages.length === 0 && <p className="text-sm text-slate-500 dark:text-zinc-500">No messages.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.direction === 'inbound' ? 'bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white' : 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 ml-auto'}`}>
                  <p className="whitespace-pre-wrap break-words">{m.preview}</p>
                  <p className="mt-1 text-[10px] opacity-60">{m.direction} · {m.status}{m.delivery_status ? ` · ${m.delivery_status}` : ''} · {fmtTime(m.created_at)}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <h2 className="font-medium text-slate-900 dark:text-white mb-3">Conversations</h2>
            <ScrollHint>
              <div className="overflow-x-auto" data-scroll-host>
                <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="text-left text-slate-500 dark:text-zinc-500 border-b border-slate-200 dark:border-zinc-800">
                    <th className="py-2 pr-4 font-medium">Phone</th>
                    <th className="py-2 pr-4 font-medium">User</th>
                    <th className="py-2 pr-4 font-medium">Flow</th>
                    <th className="py-2 pr-4 font-medium">Last inbound</th>
                    <th className="py-2 font-medium">Last outbound</th>
                  </tr>
                </thead>
                <tbody>
                  {convs.items.length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-slate-500 dark:text-zinc-500" data-testid="wa-no-conversations">No conversations yet — candidates start one by messaging your WhatsApp Business number.</td></tr>
                  )}
                  {convs.items.map((c) => (
                    <tr key={c.id} onClick={() => openConv(c.phone)} className="border-b border-slate-100 dark:border-zinc-800/60 cursor-pointer hover:bg-slate-50 dark:hover:bg-zinc-800/40" data-testid={`wa-conv-row-${c.phone}`}>
                      <td className="py-2.5 pr-4 font-mono text-slate-900 dark:text-white">+{c.phone}</td>
                      <td className="py-2.5 pr-4 text-slate-700 dark:text-zinc-300">{c.user_name || <span className="text-slate-400 dark:text-zinc-600">Unknown</span>}{c.user_role ? ` (${c.user_role})` : ''}</td>
                      <td className="py-2.5 pr-4 text-slate-500 dark:text-zinc-500">{c.flow || '—'}</td>
                      <td className="py-2.5 pr-4 text-slate-500 dark:text-zinc-500">{fmtTime(c.last_inbound_at)}</td>
                      <td className="py-2.5 text-slate-500 dark:text-zinc-500">{fmtTime(c.last_outbound_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </ScrollHint>
            <Pagination page={page} pageSize={convs.limit || 20} total={convs.total} onPageChange={setPage} testIdPrefix="wa-pagination" />
          </>
        )}
      </div>
    </div>
  );
}
